/* import __COLOCATED_TEMPLATE__ from './billing.hbs'; */
import Component from '@glimmer/component';
import { tracked } from '@glimmer/tracking';
import { action } from '@ember/object';
import { task, waitForProperty } from 'ember-concurrency';
import { handleErrorResponseWithModal } from 'sh-www/utils/handles-errors';
import { COMPANY } from 'sh-www/models/company';
import clsx from 'clsx';

const CLASS_NAME = 'SH-c-admin-company-billing';

const resolveKey = (context, path) => {
  return path.split('.').reduce((prev, curr) => prev?.[curr], context) ?? null;
};

const changedFromDefault = (context, moduleKey) => {
  const { defaultValues } = context;
  const currentValues = resolveKey(context, moduleKey);
  if (!currentValues) return {};

  // do not mark as changed until the default values have been established
  const defaultsAreSet = Object.keys(defaultValues).length > 0;
  return Object.keys(currentValues).reduce((accum, currentValueKey) => {
    // compare the values at the resolved key. not comparing strict due to certain string/number combinations
    accum[currentValueKey] = (
      defaultsAreSet
      && currentValues[currentValueKey] != resolveKey(defaultValues, moduleKey)[currentValueKey]
    );
    return accum;
  }, {});
}

const getDeepKeys = (obj) => {
  let keys = [];
  for (const key in obj) {
    keys.push(key);
    if (typeof obj[key] === 'object' && obj[key] !== null) {
      const subkeys = getDeepKeys(obj[key]);
      subkeys.reduce((accum, subkey) => {
        accum.push(`${key}.${subkey}`);
        return accum;
      }, keys);
    }
  }
  return keys;
}

const formatPercent = (num) => {
  const percent = num * 100;
  return percent % 1 != 0 ? percent.toFixed(2) : percent;
}

class AdminCompanyBilling extends Component {
  @tracked cardSettings = {};
  @tracked company = {};
  @tracked companyBillingDefaults = [];
  @tracked companyBillingContacts = {};
  @tracked defaultValues = {};
  @tracked features;
  @tracked fees = {};
  @tracked general;
  @tracked invoiceCustomization = {};
  @tracked invoiceRouting = {};
  @tracked invoiceSettings = {};

  get className() {
    return CLASS_NAME;
  }

  get classNames() {
    return clsx(
      this.args.class,
      this.className,
    );
  }

  get isSaveDisabled() {
    return this.loadBillingDefaultsTask.isRunning
      || this.loadBillingTask.isRunning
      || this.loadCompanyContactsTask.isRunning
      || this.saveTask.isRunning
      || this.setDefaults.isRunning
      || !this.hasAnyValueChanged;
  }

  get isResetDisabled() {
    return this.loadBillingDefaultsTask.isRunning
      || this.loadBillingTask.isRunning
      || this.loadCompanyContactsTask.isRunning
      || this.saveTask.isRunning
      || this.setDefaults.isRunning
      || !this.hasAnyValueChanged;
  }

  get isBillingWithDefaultsModuleDisabled() {
    return this.loadBillingDefaultsTask.isError
      || this.loadBillingTask.isError;
  }

  get isBillingWithDefaultsModuleLoading() {
    return this.loadBillingDefaultsTask.isRunning
      || this.loadBillingTask.isRunning
      || this.loadBillingDefaultsTask.performCount === 0
      || this.loadBillingTask.performCount === 0;
  }

  get isBillingModuleLoading() {
    return this.loadBillingTask.isRunning
      || this.loadBillingTask.performCount === 0;
  }

  get isBillingDefaultModuleLoading() {
    return this.loadBillingDefaultsTask.isRunning
      || this.loadBillingTask.performCount === 0;
  }

  get isCompanyContactsModuleLoading() {
    return this.loadCompanyContactsTask.isRunning
      || this.loadCompanyContactsTask.performCount === 0;
  }

  get hasCardOnFile() {
    return this.company.companyBilling?.isBillable;
  }

  get hasAnyValueChanged() {
    const { changedValues } = this;
    const allKeys = getDeepKeys(changedValues);

    return allKeys.some((key) => resolveKey(changedValues, key) === true);
  }

  get changedValues() {
    return {
      cardSettings: changedFromDefault(this, 'cardSettings'),
      features: changedFromDefault(this, 'features'),
      fees: changedFromDefault(this, 'fees'),
      general: changedFromDefault(this, 'general'),
      invoiceCustomization: changedFromDefault(this, 'invoiceCustomization'),
      invoiceRouting: Object.keys(this.invoiceRouting).reduce((accum, companyContactId) => {
        accum[companyContactId] = changedFromDefault(this, `invoiceRouting.${companyContactId}`);
        return accum;
      }, {}),
      invoiceSettings: changedFromDefault(this, 'invoiceSettings'),
    };
  }

  @task
  *saveTask(company) {
    const jsonData = Object.keys(this.changedValues).reduce((accum, moduleKey) => {
      accum[moduleKey] = this[moduleKey];

      return accum;
    }, {});


    try {
      yield this.args.saveCompanyBillingSettings.perform(company, jsonData);

      this.loadBillingDefaultsTask.perform();
      this.loadBillingTask.perform(company);
      this.loadCompanyContactsTask.perform(company);
      this.setDefaults.perform();
    } catch(e) {
      handleErrorResponseWithModal(this, e);
    }
  }

  /**
   * Store the default values of each module after each task has finished running
   */
  @task
  *setDefaults() {
    yield waitForProperty(this, 'loadBillingDefaultsTask.isIdle');
    yield waitForProperty(this, 'loadBillingTask.isIdle');
    yield waitForProperty(this, 'loadCompanyContactsTask.isIdle');

    // store the values that could've changed
    this.defaultValues = {
      cardSettings: this.cardSettings,
      features: this.features,
      fees: this.fees,
      general: this.general,
      invoiceCustomization: this.invoiceCustomization,
      invoiceRouting: this.invoiceRouting,
      invoiceSettings: this.invoiceSettings,
    };
  }

  @task
  *loadBillingTask(company) {
    try {
      this.company = yield this.args.loadCompanyBillingTask.linked().perform(company);

      const { companyBilling } = this.company;
      this.cardSettings = {
        stripeId: companyBilling.stripeId,
      }
      this.fees = {
        advanceFee: formatPercent(companyBilling.advanceFee),
        delinquentFee: formatPercent(companyBilling.delinquentFee),
        expenseFee: formatPercent(companyBilling.expenseFee),
        finderFee: formatPercent(companyBilling.finderFee),
        inviteeFee: formatPercent(companyBilling.inviteeFee),
        selectBaseFee: formatPercent(companyBilling.selectBaseFee),
        selectFee: formatPercent(companyBilling.selectFee),
      };
      this.invoiceSettings = {
        onInvoice: companyBilling.onInvoice,
        invoiceNet: companyBilling.invoiceNet,
        autocharge: companyBilling.autocharge,
      }
      this.invoiceCustomization = {
        billableName: companyBilling.billableName,
        billableText: companyBilling.billableText,
        paymentInstructionsText: companyBilling.paymentInstructionsText,
      }
    } catch (e) {
      handleErrorResponseWithModal(this, e);
    }
  }

  @task
  *loadBillingDefaultsTask() {
    try {
      this.companyBillingDefaults = yield this.args.loadDefaultFeesTask.perform();
    } catch (e) {
      handleErrorResponseWithModal(this, e);
    }
  }

  @task
  *loadCompanyContactsTask(company) {
    try {
      this.companyBillingContacts = yield this.args.loadCompanyContactsTask.perform(company);

      // get any deleted cost codes that may exist on a billing contact
      const inactiveCostCodes = Object.values(this.companyBillingContacts).reduce((accum, { costCodes }) => {
        costCodes.forEach(costCode => {
          if (!accum.includes(costCode) && costCode.deletedAt && !costCode.hidden) {
            accum.push(costCode);
          }
        });
        return accum;
      }, []);

      const allCostCodes = [
        ...inactiveCostCodes,
        ...company.costCodes.toArray(),
      ];

      // include all cost codes in final data structure
      const companyCostCodesBaseMap = allCostCodes.reduce((accum, { id }) => {
        accum[id] = false;
        return accum;
      }, {});

      // set default values
      this.invoiceRouting = this.companyBillingContacts.reduce((accum, companyContact) => {
        const costCodeEnabledMap = companyContact.costCodes.reduce((acc, costCode) => {
          acc[costCode.id] = true;
          return acc;
        }, { ...companyCostCodesBaseMap });

        accum[companyContact.id] = costCodeEnabledMap;

        return accum;
      }, {});
    } catch (e) {
      handleErrorResponseWithModal(this, e);
    }
  }

  @task
  *onUpdateCompanyContactTask(companyContact, newEmail) {
    const { company } = this;
    try {
      yield this.args.onUpdateCompanyContactTask.perform(companyContact, newEmail);
      yield this.reloadCompanyContactsTask.perform(company);
    } catch (e) {
      handleErrorResponseWithModal(this, e);
    }
  }

  @task
  *onDeleteCompanyContactTask(companyContact) {
    const { company } = this;
    yield this.args.onDeleteCompanyContactTask.perform(companyContact);
    yield this.reloadCompanyContactsTask.perform(company);
  }

  @task
  *reloadCompanyContactsTask(company) {
    yield this.loadCompanyContactsTask.perform(company);
    yield this.setDefaults.perform();
  }

  @action
  initializeData() {
    const { company } = this.args;

    const { features } = company;
    this.features = {
      advances: features.advances | 0,
      always_cover_fees: features.always_cover_fees | 0,
      brands: features.brands | 0,
      can_search: features.can_search | 0,
      credits: features.credits | 0,
      direct_message: features.direct_message | 0,
      expenses: features.expenses | 0,
      invite_max: features.invite_max || null,
      lockout_override: features.lockout_override | 0,
      public_projects: features.public_projects | 0,
      reports: features.reports | 0,
      requires_cost_codes: features.requires_cost_codes | 0,
      requires_posting_approval: features.requires_posting_approval | 0,
      uses_company_teams: features.uses_company_teams | 0,
      uses_cost_codes: features.uses_cost_codes | 0,
    };
    this.general = {
      tier: company.tier,
      tierVersion: company.tierVersion ?? COMPANY.TIER_VERSIONS.TWO,
    };

    this.loadBillingDefaultsTask.perform();
    this.loadBillingTask.perform(company);
    this.loadCompanyContactsTask.perform(company);
    this.setDefaults.perform();
  }

  @action
  handleChangeGeneral(key, value) {
    this.general = {
      ...this.general,
      [key]: value,
    };

    const availableTiers = COMPANY.VERSION_TIERS[this.general.tierVersion];
    if (key === 'tierVersion' && !availableTiers[this.general.tier]) {
      this.general.tier = COMPANY.TIERS.FREE;
    }

    const { tier, tierVersion } = this.general;

    // all features and fee values should be updated to the defaults for the new tier
    if (key === 'tier' || key === 'tierVersion') {
      const tierDefaults = this.companyBillingDefaults[tierVersion][tier];

      this.fees = {
        advanceFee: formatPercent(tierDefaults.advanceFee),
        cancelFee: formatPercent(tierDefaults.cancelFee),
        delinquentFee: formatPercent(tierDefaults.delinquentFee),
        expenseFee: formatPercent(tierDefaults.expenseFee),
        finderFee: formatPercent(tierDefaults.finderFee),
        inviteeFee: formatPercent(tierDefaults.inviteeFee),
        selectBaseFee: formatPercent(tierDefaults.selectBaseFee),
        selectFee: formatPercent(tierDefaults.selectFee),
      };

      this.features = Object.keys(tierDefaults.features).reduce((accum, feature) => {
        accum[feature] = tierDefaults.features[feature];
        return accum;
      }, {});
    }
  }

  @action
  handleChangeCardSettings(key, value) {
    this.cardSettings = {
      ...this.cardSettings,
      [key]: value,
    };
  }

  @action
  handleUpdateFee(key, value) {
    this.fees = {
      ...this.fees,
      [key]: value,
    };
  }

  @action
  handleUpdateFeature(key, value) {
    this.features = {
      ...this.features,
      [key]: value,
    };
  }

  @action
  handleUpdateInvoiceRouting(companyContactId, costCodeId) {
    const values = this.invoiceRouting[companyContactId] ?? {};
    const currentValue = values[costCodeId] ?? false;

    this.invoiceRouting = {
      ...this.invoiceRouting,
      [companyContactId]: {
        ...values,
        [costCodeId]: !currentValue,
      },
    };
  }

  @action
  handleUpdateInvoiceSettings(key, value) {
    this.invoiceSettings = {
      ...this.invoiceSettings,
      [key]: value,
    };
  }

  @action
  handleUpdateInvoiceCustomization(key, value) {
    this.invoiceCustomization = {
      ...this.invoiceCustomization,
      [key]: value,
    };
  }

  @action
  resetForm() {
    Object.keys(this.defaultValues).forEach(key => {
      this[key] = this.defaultValues[key];
    });
  }
}

export default AdminCompanyBilling;
